import React, { useEffect, useMemo, useRef } from 'react';

// Leaflet is loaded from a CDN on demand (pinned version + Subresource
// Integrity), matching LocationMap so no build dependency is added.
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_CSS_SRI = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const LEAFLET_JS_SRI = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';

let leafletPromise = null;
const loadLeaflet = () => {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-leaflet]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.integrity = LEAFLET_CSS_SRI;
      link.crossOrigin = '';
      link.setAttribute('data-leaflet', '1');
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.integrity = LEAFLET_JS_SRI;
    script.crossOrigin = '';
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Failed to load Leaflet'));
    document.head.appendChild(script);
  });
  return leafletPromise;
};

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// One current-position marker per device (no track/trail).
const DevicesMap = ({ devices }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const leafletRef = useRef(null);
  const fittedKeyRef = useRef('');

  // Current fix per device from properties.location ({latitude, longitude, time}).
  const markers = useMemo(() => {
    const out = [];
    for (const d of devices || []) {
      const loc = d?.properties?.location;
      if (!loc || typeof loc !== 'object') continue;
      const lat = Number(loc.latitude);
      const lng = Number(loc.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      out.push({
        uuid: d.uuid,
        name: d?.properties?.name || d.uuid,
        online: d.connected === true,
        time: loc.time,
        lat,
        lng,
      });
    }
    return out;
  }, [devices]);

  // Create the map once and rebuild the markers whenever the fixes change.
  useEffect(() => {
    if (markers.length === 0) return undefined;
    let disposed = false;

    loadLeaflet()
      .then((L) => {
        if (disposed || !containerRef.current) return;
        leafletRef.current = L;

        if (!mapRef.current) {
          mapRef.current = L.map(containerRef.current, { scrollWheelZoom: false });
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 19,
          }).addTo(mapRef.current);
        }

        const map = mapRef.current;

        if (layerRef.current) {
          map.removeLayer(layerRef.current);
          layerRef.current = null;
        }

        const group = L.layerGroup();
        markers.forEach((m) => {
          const color = m.online ? '#16a34a' : '#9ca3af';
          const marker = L.circleMarker([m.lat, m.lng], {
            radius: 8,
            color: '#ffffff',
            weight: 2,
            fillColor: color,
            fillOpacity: 1,
          });
          const when = m.time
            ? (() => { const dt = new Date(m.time); return isNaN(dt.getTime()) ? String(m.time) : dt.toLocaleString(); })()
            : '';
          marker.bindPopup(
            `<strong>${escapeHtml(m.name)}</strong><br/>${m.online ? '● Online' : '○ Offline'}`
            + `<br/>${m.lat.toFixed(6)}, ${m.lng.toFixed(6)}`
            + (when ? `<br/>${escapeHtml(when)}` : '')
          );
          marker.bindTooltip(
            when ? `${escapeHtml(m.name)} · updated ${escapeHtml(when)}` : escapeHtml(m.name),
            { direction: 'top', offset: [0, -8] }
          );
          marker.addTo(group);
        });
        group.addTo(map);
        layerRef.current = group;

        // Only (re)fit when the set of plotted devices changes, so live position
        // nudges don't fight the user's current zoom/pan.
        const key = markers.map((m) => m.uuid).sort().join('|');
        if (key !== fittedKeyRef.current) {
          fittedKeyRef.current = key;
          const latlngs = markers.map((m) => [m.lat, m.lng]);
          if (latlngs.length === 1) map.setView(latlngs[0], 15);
          else map.fitBounds(L.latLngBounds(latlngs), { padding: [24, 24], maxZoom: 16 });
        }

        setTimeout(() => { if (!disposed && mapRef.current) mapRef.current.invalidateSize(); }, 0);
      })
      .catch(() => {});

    return () => { disposed = true; };
  }, [markers]);

  // Keep the map sized correctly when the container grows (hidden tab, resize).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (mapRef.current && el.clientWidth > 0 && el.clientHeight > 0) {
        mapRef.current.invalidateSize();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Tear down on unmount.
  useEffect(() => () => {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      layerRef.current = null;
    }
  }, []);

  const headerStyle = {
    fontSize: '0.7rem',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '10px',
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <div style={headerStyle}>📍 Device locations</div>
      {markers.length === 0 ? (
        <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>
          No location data available for the shown devices.
        </p>
      ) : (
        <>
          <div
            ref={containerRef}
            style={{ height: '320px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}
          />
          <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: '8px 0 0' }}>
            {markers.length} device{markers.length === 1 ? '' : 's'} with a location · updates live
          </p>
        </>
      )}
    </div>
  );
};

export default DevicesMap;
